import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin } from '../../../api/productApi';
import styles from './AdminLogin.module.scss';

export const AdminLogin = () => {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        login: false,
        password: false
    });
    const navigate = useNavigate();

    const validateFields = () => {
        const errors = {
            login: !login.trim(),
            password: !password.trim()
        };
        setFieldErrors(errors);
        return !errors.login && !errors.password;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!validateFields()) {
            setError('Заполните все обязательные поля');
            return;
        }
        setIsLoading(true);
        try {
            const { token } = await adminLogin({
                login,
                password
            });
            localStorage.setItem('adminToken', token);
            navigate('/admin/dashboard');
        } catch (err) {
            setError('Неверный логин или пароль');
            console.error('Ошибка авторизации:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const clearLogin = () => setLogin('');
    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} className={styles.form}>
                <h1 className={styles.title}>Вход в админ-панель</h1>
                {error && (
                    <div className={styles.errorInline}>
                        <svg className={styles.errorInlineIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 6.66667V10" stroke="#FF4D4F" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M10 13.3333H10.0083" stroke="#FF4D4F" strokeWidth="1.5" strokeLinecap="round"/>
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M1.6665 10C1.6665 5.39763 5.39746 1.66667 9.99984 1.66667C14.6022 1.66667 18.3332 5.39763 18.3332 10C18.3332 14.6024 14.6022 18.3333 9.99984 18.3333C5.39746 18.3333 1.6665 14.6024 1.6665 10Z"
                                  stroke="#FF4D4F" strokeWidth="1.5"/>
                        </svg>
                        <span className={styles.errorInlineText}>{error}</span>
                    </div>
                )}
                <div className={styles.field}>
                    <label className={styles.label}>Логин</label>
                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            className={`${styles.input} ${fieldErrors.login ? styles.errorInput : ''}`}
                            placeholder="Введите логин..."
                            value={login}
                            onChange={(e) => {
                                setLogin(e.target.value);
                                setFieldErrors({...fieldErrors, login: false});
                            }}
                            disabled={isLoading}
                        />
                        {login && !isLoading && (
                            <button
                                type="button"
                                className={styles.clearButton}
                                onClick={clearLogin}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 1L1 13" stroke="#A3BABA" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M1 1L13 13" stroke="#A3BABA" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                    {fieldErrors.login && (
                        <span className={styles.fieldError}>Поле обязательно для заполнения</span>
                    )}
                </div>
                <div className={styles.field}>
                    <label className={styles.label}>Пароль</label>
                    <div className={styles.inputWrapper}>
                        <input
                            type={showPassword ? "text" : "password"}
                            className={`${styles.input} ${fieldErrors.password ? styles.errorInput : ''}`}
                            placeholder="Введите пароль..."
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setFieldErrors({...fieldErrors, password: false});
                            }}
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className={styles.toggleButton}
                            onClick={togglePasswordVisibility}
                            disabled={isLoading}
                        >
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M3.13477 15.498C2.95377 15.193 2.95377 14.807 3.13477 14.502C5.03977 11.279 8.51977 8.5 11.9998 8.5C15.4798 8.5 18.9588 11.279 20.8638 14.501C21.0448 14.807 21.0448 15.194 20.8638 15.5C18.9588 18.721 15.4798 21.5 11.9998 21.5C8.51977 21.5 5.03977 18.721 3.13477 15.498Z" stroke="#A3BABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14.121 12.879C15.293 14.051 15.293 15.95 14.121 17.122C12.949 18.294 11.05 18.294 9.87802 17.122C8.70602 15.95 8.70602 14.051 9.87802 12.879C11.05 11.707 12.95 11.707 14.121 12.879" stroke="#A3BABA" strokeWidth="1.4286" strokeLinecap="round" strokeLinejoin="round"/>
                                {showPassword ? (
                                    <path d="M19 5L5 19" stroke="#A3BABA" strokeWidth="2" strokeLinecap="round"/>
                                ) : (
                                    <>
                                        <path d="M12 3V5.5" stroke="#A3BABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M5 5L6.68 7" stroke="#A3BABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M18.9993 5L17.3193 7" stroke="#A3BABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                    {fieldErrors.password && (
                        <span className={styles.fieldError}>Поле обязательно для заполнения</span>
                    )}
                </div>
                <div className={styles.footer}>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <svg className={styles.spinner} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 1.66667V5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M15.774 4.22601L13.3333 6.66667" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M18.3333 10H15" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M15.774 15.774L13.3333 13.3333" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M10 18.3333V15" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M4.22601 15.774L6.66667 13.3333" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M1.66667 10H5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M4.22601 4.22601L6.66667 6.66667" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                                Вход...
                            </>
                        ) : 'Войти'}
                    </button>
                </div>
            </form>
        </div>
    );
};
